import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { getAIResponse } from '../services/openaiService';
import Header from './Header';
import Footer from './Footer'
import './InterviewPage.css';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
import mammoth from 'mammoth';
import ProductSidebar from './ProductSidebar';
import { createDoc, PDF, fR, sR, T, drawCoverHeader, drawPageHeader, drawFooters, drawMetaCard, drawSectionHeading } from '../utils/pdfBrand';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Add pulse animation styles
const styles = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.2);
    }
  }
`;

// Inject styles into document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  if (!document.head.querySelector('style[data-pulse-animation]')) {
    styleSheet.setAttribute('data-pulse-animation', 'true');
    document.head.appendChild(styleSheet);
  }
}

const formatCodeInResponse = (text) => {
  const codeBlockRegex = /```(\w+)?\s*([\s\S]*?)```/g;
  return text.replace(codeBlockRegex, (match, language, code) => {
    return `<div class="code-block">${code.trim()}</div>`;
  });
};

const InterviewPage = () => {
  const navigate = useNavigate();
  const { user, loading, logout, credits } = useUser();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
  const isElectronApp = window.navigator.userAgent.toLowerCase().includes('electron');
  const isWindows = navigator.userAgent.toLowerCase().includes('windows');
  const loopbackDeviceName = isWindows ? 'VB-Cable' : 'BlackHole';

  const [showJobForm, setShowJobForm] = useState(false);
  const [formData, setFormData] = useState({
    jobRole: '',
    experienceLevel: '',
    company: '',
    jobDescription: '',
    resumeText: ''
  });
  const [uploadedResumeFile, setUploadedResumeFile] = useState(null);
  const [uploadedJobDescFile, setUploadedJobDescFile] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [resumeChoice, setResumeChoice] = useState(null); // 'profile' | 'upload'
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [fileUploadError, setFileUploadError] = useState('');
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenShareError, setScreenShareError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(30 * 60);
  const sessionDuration = (credits >= 999000) ? 90 * 60 : 30 * 60;
  const [interviewSessionId, setInterviewSessionId] = useState(null);
  const [isAutoMode, setIsAutoMode] = useState(false);
  const [isAutoRecording, setIsAutoRecording] = useState(false);
  const [autoRecordingError, setAutoRecordingError] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const conversationContainerRef = useRef(null);
  const prevConvLengthRef = useRef(0);
  const screenShareRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const autoRecognitionRef = useRef(null);
  const autoAudioContextRef = useRef(null);
  const autoStreamRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const audioProcessorRef = useRef(null);
  const restartAttemptsRef = useRef(0);
  const maxRestartAttempts = 3;
  const isProcessingRef = useRef(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isAlwaysOnTop, setIsAlwaysOnTop] = useState(true);
  let conversationIdCounter = useRef(0);

  const [micActive, setMicActive] = useState(false);
  const micRecorderRef = useRef(null);
  const micChunksRef = useRef([]);
  const [fontSize, setFontSize] = useState(15);


  const buildResumeTextFromProfile = (profile) => {
    let text = '';
    if (profile.fullName) text += `${profile.fullName}\n`;
    if (profile.jobTitle) text += `${profile.jobTitle}\n`;
    if (profile.phone) text += `Phone: ${profile.phone}\n`;
    if (profile.location) text += `Location: ${profile.location}\n`;
    if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
    if (profile.github) text += `GitHub: ${profile.github}\n`;
    text += '\n';
    if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
    if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
    if (Array.isArray(profile.workExperience) && profile.workExperience.some(e => e.company || e.position)) {
      text += 'WORK EXPERIENCE\n';
      profile.workExperience.forEach(exp => {
        if (exp.position || exp.company) {
          text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
          if (exp.description) text += `${exp.description}\n`;
          text += '\n';
        }
      });
    }
    if (Array.isArray(profile.education) && profile.education.some(e => e.degree || e.institution)) {
      text += 'EDUCATION\n';
      profile.education.forEach(edu => {
        if (edu.degree || edu.institution) {
          text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
        }
      });
      text += '\n';
    }
    if (Array.isArray(profile.projects) && profile.projects.some(p => p.name)) {
      text += 'PROJECTS\n';
      profile.projects.forEach(proj => {
        if (proj.name) {
          text += `${proj.name}${proj.technologies ? ` (${proj.technologies})` : ''}\n`;
          if (proj.description) text += `${proj.description}\n`;
          text += '\n';
        }
      });
    }
    if (profile.certifications) text += `CERTIFICATIONS\n${profile.certifications}\n`;
    return text.trim();
  };

  // Function to extract text from PDF
  const extractTextFromPDF = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      }

      return fullText.trim();
    } catch (error) {
      console.error('Error parsing PDF:', error);
      throw new Error('Failed to parse PDF file. Please ensure the PDF contains readable text.');
    }
  };

  // Function to extract text from DOCX
  const extractTextFromDOCX = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      console.error('Error parsing DOCX:', error);
      throw new Error('Failed to parse DOCX file. Please try pasting the text manually.');
    }
  };

  // Function to extract text from DOC
  const extractTextFromDOC = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      console.error('Error parsing DOC:', error);
      throw new Error('Failed to parse DOC file. This format may not be fully supported. Please try converting to DOCX or pasting the text manually.');
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsProcessingFile(true);
    setFileUploadError('');

    try {
      if (type === 'resume') {
        setUploadedResumeFile(file);
      } else if (type === 'jobDesc') {
        setUploadedJobDescFile(file);
      }

      let extractedText = '';

      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        const reader = new FileReader();
        extractedText = await new Promise((resolve, reject) => {
          reader.onload = (event) => resolve(event.target.result);
          reader.onerror = (error) => reject(error);
          reader.readAsText(file);
        });
      } else if (
        file.type === 'application/pdf' || 
        file.type === 'application/x-pdf' ||
        file.type === 'binary/octet-stream' ||
        file.name.toLowerCase().endsWith('.pdf')
      ) {
        extractedText = await extractTextFromPDF(file);
      } else if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/octet-stream' ||
        file.name.toLowerCase().endsWith('.docx')
      ) {
        extractedText = await extractTextFromDOCX(file);
      } else if (
        file.type === 'application/msword' || 
        file.name.toLowerCase().endsWith('.doc')
      ) {
        extractedText = await extractTextFromDOC(file);
      } else {
        throw new Error('Unsupported file format. Please upload .txt, .pdf, .doc, or .docx files.');
      }

      extractedText = extractedText.trim();

      if (!extractedText) {
        throw new Error('No text could be extracted from the file. Please ensure the file contains readable text.');
      }

      if (type === 'resume') {
        setFormData(prevData => ({
          ...prevData,
          resumeText: extractedText
        }));
        setFileUploadError('✓ Resume uploaded successfully!');
      } else if (type === 'jobDesc') {
        setFormData(prevData => ({
          ...prevData,
          jobDescription: extractedText
        }));
        setFileUploadError('✓ Job description uploaded successfully!');
      }

      setTimeout(() => {
        setFileUploadError('');
      }, 3000);

    } catch (error) {
      console.error('Error processing file:', error);
      setFileUploadError(error.message || 'Failed to process file. Please try pasting the text manually.');
      
      if (type === 'resume') {
        setUploadedResumeFile(null);
      } else if (type === 'jobDesc') {
        setUploadedJobDescFile(null);
      }
    } finally {
      setIsProcessingFile(false);
    }
  };

  const stopAutoRecording = () => {
    console.log('🛑 Stopping auto recording...');
    
    if (autoRecognitionRef.current?.mediaRecorder) {
      try {
        if (autoRecognitionRef.current.mediaRecorder.state !== 'inactive') {
          autoRecognitionRef.current.mediaRecorder.stop();
        }
      } catch (error) {
        console.warn('Error stopping media recorder:', error);
      }
    }

    if (autoStreamRef.current) {
      autoStreamRef.current.getTracks().forEach(track => track.stop());
      autoStreamRef.current = null;
    }

    if (autoAudioContextRef.current) {
      try {
        autoAudioContextRef.current.close();
      } catch (error) {
        console.warn('Error closing audio context:', error);
      }
      autoAudioContextRef.current = null;
    }

    if (audioProcessorRef.current) {
      try {
        audioProcessorRef.current.disconnect();
      } catch (error) {
        console.warn('Error disconnecting audio processor:', error);
      }
      audioProcessorRef.current = null;
    }

    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    autoRecognitionRef.current = null;
    restartAttemptsRef.current = 0;
    setIsAutoRecording(false);
    console.log('✅ Auto recording stopped');
  };

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetch(`${API_BASE_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.profile) setProfileData(data.profile); })
        .catch(() => {});
    }
  }, [user, API_BASE_URL]);

  useEffect(() => {
    if (conversation.length !== prevConvLengthRef.current) {
      prevConvLengthRef.current = conversation.length;
      setTimeout(() => {
        if (conversationContainerRef.current) {
          conversationContainerRef.current.scrollTop = -conversationContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [conversation]);

  useEffect(() => {
    return () => {
      stopAutoRecording();
    };
  }, []);

  useEffect(() => {
    if (interviewStarted && timeRemaining > 0) {
      timerIntervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleEndInterview(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [interviewStarted, timeRemaining]);

  useEffect(() => {
    if (interviewStarted && isAutoMode) {
      startAutoRecording();
    } else {
      stopAutoRecording();
    }
  }, [interviewStarted, isAutoMode]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (interviewStarted) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [interviewStarted]);

  useEffect(() => {
    if (isElectronApp) {
      document.body.classList.add('electron-app');
    }
    return () => {
      document.body.classList.remove('electron-app');
    };
  }, [isElectronApp]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleNavigateToHome = () => {
    navigate('/');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleHeaderNavigation = async (path) => {
    if (interviewStarted) {
      // Don't show confirmation for logout-redirect (already confirmed in Header)
      if (path === '/logout-redirect') {
        try {
          // Save the interview first
          if (conversation.length > 0) {
            await saveInterviewSession(false);
            console.log('✅ Interview session saved successfully');
          }
          
          // Reset interview state
          setInterviewStarted(false);
          stopAutoRecording();
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
          }
          
          // Call backend logout API first
          try {
            const response = await fetch(`${API_BASE_URL}/api/logout`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({ email: user?.email })
            });

            const data = await response.json();
            
            if (response.ok) {
              console.log('Logout successful:', data);
            } else {
              console.log('Logout API error, but proceeding with frontend logout:', data.message);
            }
          } catch (error) {
            console.error('Logout API error, but proceeding with frontend logout:', error);
          }
          
          // Then trigger frontend logout
          logout();
          navigate('/');
        } catch (error) {
          console.error('Error ending interview:', error);
          alert('Failed to save interview session.');
        }
        return;
      }

      // For other navigation, show confirmation
      const confirmEnd = window.confirm(
        'Interview is currently in progress. Do you want to end the interview and save your session?'
      );
      if (confirmEnd) {
        try {
          // Save the interview first
          if (conversation.length > 0) {
            await saveInterviewSession(false);
            console.log('✅ Interview session saved successfully');
          }
          
          // Reset interview state
          setInterviewStarted(false);
          stopAutoRecording();
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
          }
          
          if (path === '/dashboard') {
            navigate('/dashboard');
          } else {
            navigate(path);
          }
        } catch (error) {
          console.error('Error ending interview:', error);
          alert('Failed to save interview session.');
        }
      }
      // If user clicks Cancel, do nothing - stay on interview page
    } else {
      // Handle special logout redirect when no interview
      if (path === '/logout-redirect') {
        // Call backend logout API
        try {
          const response = await fetch(`${API_BASE_URL}/api/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ email: user?.email })
          });

          const data = await response.json();
          
          if (response.ok) {
            console.log('Logout successful:', data);
          } else {
            console.log('Logout API error, but proceeding with frontend logout:', data.message);
          }
        } catch (error) {
          console.error('Logout API error, but proceeding with frontend logout:', error);
        }
        
        logout();
        navigate('/');
      } else {
        navigate(path);
      }
    }
  };

  if (loading) {
    return (
      <div className="neural-glass-homepage">
        <div className="neural-background"></div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          color: '#fff',
          fontSize: '1.2rem'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const deductCredit = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/interview-assistant/deduct-credit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to deduct credit');
      }

      const data = await response.json();
      return data.remainingCredits;
    } catch (error) {
      console.error('Error deducting credit:', error);
      throw error;
    }
  };

  const handleStartInterview = async () => {
  if (formData.jobRole && formData.experienceLevel && formData.jobDescription && formData.resumeText) {
    try {
      await deductCredit();
      setInterviewStarted(true);
      setShowJobForm(false);
      setTimeRemaining(sessionDuration);

    } catch (error) {
        if (error.message?.includes('credit') || error.message?.includes('Credit')) {
          setShowJobForm(false); // close job form first so modal is visible
          setShowInsufficientCreditsModal(true);
        } else {
          alert(error.message || 'Failed to start interview. Please check your credits and try again.');
        }
      }
    } else {
      alert('Please complete all required fields before starting the interview.');
    }
  };

  const handleEditJobInfo = () => {
    setIsEditing(true);
    setShowJobForm(true);
  };

  const generateAIResponse = async (interviewerQuestion) => {
    setIsLoading(true);
    setCurrentQuestion(interviewerQuestion);

    // ✅ Add question and placeholder immediately
    const questionId = ++conversationIdCounter.current;
    const aiId = ++conversationIdCounter.current;

    setConversation(prev => [
      {
        id: questionId,
        sender: 'interviewer',
        message: interviewerQuestion,
        timestamp: new Date().toLocaleTimeString()
      },
      {
        id: aiId,
        sender: 'ai',
        message: '',  // ✅ Start empty, will fill word by word
        timestamp: new Date().toLocaleTimeString(),
        formattedMessage: ''
      },
      ...prev
    ]);

    try {
      const chronologicalConversation = [...conversation].reverse();
      const recentConversation = chronologicalConversation.slice(-4);

      // ✅ Pass streaming callback
      await getAIResponse(
        interviewerQuestion,
        formData,
        recentConversation,
        (streamedText) => {
          // ✅ Update the AI message word by word in real time
          setConversation(prev => prev.map(msg =>
            msg.id === aiId
              ? {
                  ...msg,
                  message: streamedText,
                  formattedMessage: formatCodeInResponse(streamedText)
                }
              : msg
          ));
        }
      );

    } catch (error) {
      console.error('Error generating AI response:', error);

      // ✅ Update placeholder with error message
      setConversation(prev => prev.map(msg =>
        msg.id === aiId
          ? {
              ...msg,
              message: "I apologize, but I'm having trouble generating a response. Could you please try again?",
              formattedMessage: "I apologize, but I'm having trouble generating a response. Could you please try again?"
            }
          : msg
      ));
    } finally {
      setIsLoading(false);
      setCurrentQuestion('');
    }
  };

  const handleAudioTranscript = async (transcript) => {
    if (transcript.trim()) {
      await generateAIResponse(transcript);
    }
  };

  const startAutoRecording = async () => {
    const isValidInterviewQuestion = (transcript) => {
      const text = transcript.toLowerCase().trim();
      
      if (text.length < 15) {
        console.log('🚫 Too short (likely noise):', text);
        return false;
      }
      
      const noisePatterns = [
        /^[\s\.\,\!\?\-]+$/,
        /^[a-z]\s*$/i,
        /^(uh+|um+|hmm+|mhm+|uh huh|mm hmm)[\s\.\,]*$/i,
        /^\[.*\]$/,
        /^music$/i,
        /^silence$/i,
        /^\*.*\*$/,
      ];
      
      if (noisePatterns.some(pattern => pattern.test(text))) {
        console.log('🚫 Noise pattern detected:', text);
        return false;
      }
      
      const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
      if (wordCount < 5) {
        console.log('🚫 Too few words (likely noise):', text, '- Word count:', wordCount);
        return false;
      }
      
      console.log('✅ Valid transcript detected:', text);
      return true;
    };

    try {
      setAutoRecordingError('');
      console.log('🎤 Starting auto recording...');

      let stream;
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();

        // Mac → BlackHole, Windows → VB-Cable Output (the loopback capture device)
        const loopbackDevice = devices.find(device => {
          if (device.kind !== 'audioinput') return false;
          const label = device.label.toLowerCase();
          if (isWindows) {
            return label.includes('cable output') || label.includes('vb-audio') || label.includes('voicemeeter');
          }
          return label.includes('blackhole');
        });

        if (loopbackDevice) {
          console.log(`✅ Found ${loopbackDeviceName} device:`, loopbackDevice.label);
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: { exact: loopbackDevice.deviceId },
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false
            }
          });
        } else {
          console.warn(`⚠️ ${loopbackDeviceName} not found, using default audio input`);
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false
            }
          });
        }
      } catch (err) {
        console.error('Error getting specific device, falling back to default:', err);
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        });
      }

      autoStreamRef.current = stream;
      setIsAutoRecording(true);
      console.log('✅ Audio stream acquired');

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      let audioChunks = [];
      let isRecording = false;
      let silenceTimer = null;
      let recordingTimer = null;
      let lastSoundTime = Date.now();

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      autoAudioContextRef.current = audioContext;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const VOLUME_THRESHOLD = 20;
      const SILENCE_DURATION = 1000;
      const MAX_RECORDING_DURATION = 45000;
      const MIN_RECORDING_DURATION = 500;

      const checkAudioLevel = () => {
        if (!autoStreamRef.current) return;

        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / bufferLength;

        if (average > VOLUME_THRESHOLD) {
          lastSoundTime = Date.now();
          
          if (silenceTimer) {
            clearTimeout(silenceTimer);
            silenceTimer = null;
          }

          if (!isRecording && mediaRecorder.state === 'inactive' && !isProcessingRef.current) {
            console.log('🗣️ Speech detected, starting recording...');
            audioChunks = [];
            mediaRecorder.start();
            isRecording = true;

            recordingTimer = setTimeout(() => {
              if (isRecording && mediaRecorder.state === 'recording') {
                console.log('⏱️ Max recording duration reached');
                mediaRecorder.stop();
              }
            }, MAX_RECORDING_DURATION);
          } else if (isProcessingRef.current) {
            console.log('⏸️ Skipping recording - still processing previous question');
          }
        } 
        else if (isRecording && !silenceTimer) {
          const recordingDuration = Date.now() - (lastSoundTime - SILENCE_DURATION);
          
          if (recordingDuration >= MIN_RECORDING_DURATION) {
            silenceTimer = setTimeout(() => {
              if (isRecording && mediaRecorder.state === 'recording') {
                console.log('🤫 Silence detected, stopping recording...');
                mediaRecorder.stop();
              }
            }, SILENCE_DURATION);
          }
        }

        if (autoStreamRef.current) {
          requestAnimationFrame(checkAudioLevel);
        }
      };

      checkAudioLevel();

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      }; 

      mediaRecorder.onstop = async () => {
        isRecording = false;
        
        if (silenceTimer) {
          clearTimeout(silenceTimer);
          silenceTimer = null;
        }
        if (recordingTimer) {
          clearTimeout(recordingTimer);
          recordingTimer = null;
        }

        if (audioChunks.length > 0) {
          if (isProcessingRef.current) {
            console.log('⚠️ Already processing, skipping this audio chunk');
            audioChunks = [];
            return;
          }

          console.log('📦 Processing audio chunks...');
          const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
          
          isProcessingRef.current = true;
          
          try {
            const transcript = await transcribeAudioWithWhisper(audioBlob);
            if (transcript && transcript.trim()) {
              console.log('✅ Transcribed:', transcript);
              
              if (isValidInterviewQuestion(transcript)) {
                await handleAudioTranscript(transcript);
              } else {
                console.log('⚠️ Filtered out invalid/noise transcript:', transcript);
              }
            }
          } catch (error) {
            console.error('❌ Transcription error:', error);
            setAutoRecordingError('Failed to transcribe audio. Please try again.');
          } finally {
            isProcessingRef.current = false;
          }
        }

        audioChunks = [];
      };

      autoRecognitionRef.current = { mediaRecorder, stream };
      console.log('✅ Auto recording started successfully');

    } catch (error) {
      console.error('❌ Error starting auto recording:', error);
      setAutoRecordingError(`Failed to start auto recording. Make sure microphone permissions are granted and ${loopbackDeviceName} is configured.`);
      setIsAutoRecording(false);
    }
  };

  const transcribeAudioWithWhisper = async (audioBlob) => {
    try {
      console.log('🔄 Sending audio to Whisper API...');
      console.log('Audio blob size:', audioBlob.size, 'bytes');
      console.log('Audio blob type:', audioBlob.type);
      
      const formData = new FormData();
      
      const file = new File([audioBlob], 'audio.webm', { 
        type: 'audio/webm',
        lastModified: Date.now()
      });
      
      formData.append('audio', file);

      console.log('📤 Sending file to server:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      const response = await fetch(`${API_BASE_URL}/api/interview-assistant/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        throw new Error(`Transcription failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Transcription response:', data);
      
      if (!data.success) {
        throw new Error(data.error || 'Transcription failed');
      }

      return data.transcript;
    } catch (error) {
      console.error('❌ Whisper transcription error:', error);
      throw error;
    }
  };

  const handleModeToggle = async () => {
    const newMode = !isAutoMode;
    
    if (newMode) {
      setAutoRecordingError('');
      
      const isElectron = window.navigator.userAgent.toLowerCase().includes('electron');
      
      if (isElectron) {
        const proceed = window.confirm(
          'Auto Mode uses Web Speech Recognition API which requires:\n\n' +
          '1. Active internet connection\n' +
          '2. Microphone permissions\n' +
          `3. ${loopbackDeviceName} configured as audio input\n\n` +
          'Note: Speech Recognition may not work reliably in Electron. ' +
          'Consider using Manual mode if you experience issues.\n\n' +
          'Do you want to continue with Auto Mode?'
        );
        
        if (!proceed) {
          return;
        }
      }
    }
    
    setIsAutoMode(newMode);

    if (newMode && interviewStarted) {
      await startAutoRecording();
    } else {
      stopAutoRecording();
    }
  };

  const saveInterviewSession = async (isAutoEnd = false) => {
    try {
      const duration = sessionDuration - timeRemaining;
      
      const response = await fetch(`${API_BASE_URL}/api/interview-assistant/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          jobRole: formData.jobRole,
          company: formData.company,
          experienceLevel: formData.experienceLevel,
          jobDescription: formData.jobDescription,
          resumeText: formData.resumeText,
          conversation: conversation,
          duration: duration,
          completedNaturally: isAutoEnd
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save interview session');
      }

      const data = await response.json();
      setInterviewSessionId(data.sessionId);
      return data.sessionId;
    } catch (error) {
      console.error('Error saving interview session:', error);
      alert('Failed to save interview session');
    }
  };

  const handleEndInterview = async (isAutoEnd = false) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

     stopAutoRecording();

    if (conversation.length > 0) {
      try {
        await saveInterviewSession(isAutoEnd);
        console.log('✅ Interview session saved successfully');
      } catch (error) {
        console.error('❌ Failed to save interview session:', error);
        alert('Failed to save interview session. Please try again.');
        return; // Don't navigate if save failed
      }
    }

    if (isAutoEnd) {
      alert('Time is up! Your interview session has ended and been saved.');
    }

    // Reset interview state before navigating
    setInterviewStarted(false);
    navigate('/dashboard');
  };

  const handleDownloadTranscript = () => {
    if (conversation.length === 0) {
      alert('No conversation to download yet');
      return;
    }

    const duration = sessionDuration - timeRemaining;
    const durationMinutes = Math.floor(duration / 60);
    const durationSeconds = duration % 60;
    const durationLabel = `${durationMinutes}:${String(durationSeconds).padStart(2, '0')}`;
    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const expLabel = formData.experienceLevel
      ? formData.experienceLevel.charAt(0).toUpperCase() + formData.experienceLevel.slice(1) + ' Level'
      : '';
    const statusLabel = timeRemaining <= 0 ? 'Time Ended' : 'Manually Ended';

    const doc = createDoc();
    const { W, H, M, CW } = PDF;
    let y = 0;

    const addNewPage = () => {
      doc.addPage();
      fR(doc, 255, 255, 255, 0, 0, W, H);
      drawPageHeader(doc, formData.jobRole || 'Interview');
      y = 21;
    };
    const checkBreak = (need) => { if (y + need > H - 16) addNewPage(); };

    // Page 1 white bg + cover header
    fR(doc, 255, 255, 255, 0, 0, W, H);
    drawCoverHeader(doc, {
      reportTitle: 'Interview Practice Session',
      role: formData.jobRole,
      company: formData.company,
      levelLabel: expLabel,
      dateStr,
    });
    y = 62;

    // Metadata card
    y = drawMetaCard(doc, [
      { label: 'Position', value: formData.jobRole },
      { label: 'Company', value: formData.company },
      { label: 'Experience Level', value: expLabel },
      { label: 'Date', value: dateStr },
      { label: 'Duration', value: durationLabel },
      { label: 'Total Exchanges', value: Math.floor(conversation.length / 2) },
      { label: 'Status', value: statusLabel },
    ], y) + 12;

    // Section heading
    y = drawSectionHeading(doc, 'Conversation Transcript', y);

    // Messages — chronological order, question then answer per exchange
    const reversed = [...conversation].reverse();
    const ordered = [];
    for (let i = 0; i < reversed.length; i += 2) {
      const a = reversed[i];
      const b = reversed[i + 1];
      if (a && b && a.sender === 'ai' && b.sender === 'interviewer') {
        ordered.push(b); // question first
        ordered.push(a); // answer second
      } else {
        if (a) ordered.push(a);
        if (b) ordered.push(b);
      }
    }
    ordered.forEach((item) => {
      const isUser = item.sender === 'ai';
      const label = isUser ? 'Your Response' : 'Interviewer Question';
      const msgLines = doc.splitTextToSize(item.message || '', CW - 14);
      const cardH = msgLines.length * 5 + 16;
      checkBreak(cardH + 6);

      // Card background
      if (isUser) {
        fR(doc, 246, 244, 255, M, y, CW, cardH);   // light purple
        sR(doc, 196, 181, 253, 0.3, M, y, CW, cardH);
        fR(doc, 109, 40, 217, M, y, 3.5, cardH);   // purple left bar
      } else {
        fR(doc, 255, 245, 250, M, y, CW, cardH);   // light pink
        sR(doc, 255, 182, 214, 0.3, M, y, CW, cardH);
        fR(doc, 219, 39, 119, M, y, 3.5, cardH);   // pink left bar
      }

      // Header row: label + timestamp
      T(doc, isUser ? [76, 29, 149] : [157, 23, 77], 7.5, true, label, M + 7, y + 6.5);
      if (item.timestamp) {
        T(doc, [150, 140, 165], 7, false, item.timestamp, M + CW - 3, y + 6.5, { align: 'right' });
      }

      // Message text
      T(doc, [35, 28, 55], 8, false, msgLines, M + 7, y + 12.5);
      y += cardH + 5;
    });

    drawFooters(doc);

    const filename = `terview-transcript-${(formData.jobRole || 'interview').replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
  };

  const handleMicClick = async () => {
    if (isLoading || !interviewStarted || isAutoMode) return;

    if (!micActive) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 }
        });
        micChunksRef.current = [];
        micRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
        micRecorderRef.current.ondataavailable = (e) => {
          if (e.data.size > 0) micChunksRef.current.push(e.data);
        };
        micRecorderRef.current.onstop = async () => {
          stream.getTracks().forEach(t => t.stop());
          if (!micChunksRef.current.length) return;
          const audioBlob = new Blob(micChunksRef.current, { type: 'audio/webm' });
          micChunksRef.current = [];
          try {
            const transcript = await transcribeAudioWithWhisper(audioBlob);
            if (transcript?.trim()) await handleAudioTranscript(transcript);
          } catch (e) {
            console.error('Mic transcription error:', e);
            alert('Error transcribing audio. Please try again.');
          }
        };
        micRecorderRef.current.start(1000);
        setMicActive(true);
      } catch (err) {
        console.error('Mic access error:', err);
        alert('Could not access microphone. Please check permissions.');
      }
    } else {
      if (micRecorderRef.current) {
        micRecorderRef.current.stop();
      }
      setMicActive(false);
    }
  };

  const renderMessageContent = (message, formattedMessage) => {
    if (formattedMessage && formattedMessage.includes('class="code-block"')) {
      return <div dangerouslySetInnerHTML={{ __html: formattedMessage }} />;
    }
    return message;
  };

  const handleScreenShare = async () => {
    try {
      setScreenShareError('');
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        setScreenShareError('Screen sharing is not supported in your browser');
        return;
      }
      
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always'
        },
        audio: false
      });
      
      setIsScreenSharing(true);
      
      stream.getTracks().forEach(track => {
        track.onended = () => {
          setIsScreenSharing(false);
          if (screenShareRef.current) {
            screenShareRef.current.srcObject = null;
          }
        };
      });
      
      if (screenShareRef.current) {
        screenShareRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error sharing screen:', error);
      setScreenShareError('Failed to share screen. Please try again.');
      setIsScreenSharing(false);
    }
  };

  const stopScreenShare = () => {
    if (screenShareRef.current && screenShareRef.current.srcObject) {
      const tracks = screenShareRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      screenShareRef.current.srcObject = null;
    }
    setIsScreenSharing(false);
    setScreenShareError('');
  };
  
  return (
    <div className="neural-glass-homepage">
      
      {showJobForm && (
        <div className="job-form-popup-overlay">
          <div className="job-form-popup glass">
            <div className="popup-header">
              <h3>{isEditing ? 'Edit Job Information' : 'Enter Job Information'}</h3>
              <button 
                className="close-popup" 
                onClick={() => {
                  setShowJobForm(false);
                  setIsEditing(false);
                }}
              >
                &times;
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleStartInterview();
            }}>
              <div className="form-group">
                <input
                  type="text"
                  name="jobRole"
                  placeholder="Job Role/Position *"
                  value={formData.jobRole}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <select
                  name="experienceLevel"
                  value={formData.experienceLevel}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Experience Level *</option>
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                  <option value="executive">Executive</option>
                </select>
              </div>
              
              <div className="form-group">
                <input
                  type="text"
                  name="company"
                  placeholder="Company (optional)"
                  value={formData.company}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group form-group-ip">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ color: '#e0a3ff', fontWeight: '500' }}>Job Description *</label>
                  {uploadedJobDescFile && (
                    <div style={{ 
                      fontSize: '0.85rem', 
                      color: '#9370db', 
                      padding: '6px',
                      background: 'rgba(147, 112, 219, 0.1)',
                      borderRadius: '4px'
                    }}>
                      📄 {uploadedJobDescFile.name}
                    </div>
                  )}
                  <label htmlFor="job-desc-upload" style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: isProcessingFile ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem',
                    opacity: isProcessingFile ? 0.6 : 1
                  }}>
                    {isProcessingFile ? 'Processing...' : '📄 Upload File'}
                  </label>
                  <input
                    id="job-desc-upload"
                    type="file"
                    accept=".txt,.doc,.docx,.pdf"
                    onChange={(e) => handleFileUpload(e, 'jobDesc')}
                    disabled={isProcessingFile}
                    style={{ display: 'none' }}
                  />
                </div>
                <textarea
                  name="jobDescription"
                  placeholder="Paste job description or upload a file (PDF, DOCX, DOC, TXT)"
                  value={formData.jobDescription}
                  onChange={handleInputChange}
                  rows="5"
                  required
                  disabled={isProcessingFile}
                ></textarea>
              </div>
              
              <div className="form-group form-group-ip">
                <label style={{ color: '#e0a3ff', fontWeight: '500', marginBottom: '8px', display: 'block' }}>Your Resume/CV *</label>
                {profileData && (profileData.professionalSummary || profileData.skills || (Array.isArray(profileData.workExperience) && profileData.workExperience.some(e => e.company || e.position))) ? (
                  <>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setResumeChoice('profile');
                          setFormData(prev => ({ ...prev, resumeText: buildResumeTextFromProfile(profileData) }));
                          setUploadedResumeFile(null);
                        }}
                        style={{
                          flex: 1, padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                          border: `2px solid ${resumeChoice === 'profile' ? '#9370db' : 'rgba(255,255,255,0.2)'}`,
                          background: resumeChoice === 'profile' ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.05)',
                          color: 'white', fontSize: '0.85rem', transition: 'all 0.2s', textAlign: 'left'
                        }}
                      >
                        <div style={{ fontWeight: '600' }}>✅ Use Profile Resume</div>
                        {profileData.fullName && <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '3px' }}>{profileData.fullName}</div>}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setResumeChoice('upload');
                          setFormData(prev => ({ ...prev, resumeText: '' }));
                          setUploadedResumeFile(null);
                        }}
                        style={{
                          flex: 1, padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                          border: `2px solid ${resumeChoice === 'upload' ? '#9370db' : 'rgba(255,255,255,0.2)'}`,
                          background: resumeChoice === 'upload' ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.05)',
                          color: 'white', fontSize: '0.85rem', transition: 'all 0.2s', textAlign: 'left'
                        }}
                      >
                        <div style={{ fontWeight: '600' }}>📄 Upload New Resume</div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '3px' }}>Choose a different file</div>
                      </button>
                    </div>
                    {resumeChoice === 'profile' && (
                      <div style={{
                        padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem',
                        background: 'rgba(147,112,219,0.1)', border: '1px solid rgba(147,112,219,0.3)', color: '#e0a3ff'
                      }}>
                        ✓ Your profile resume will be used for this interview session.
                      </div>
                    )}
                    {resumeChoice === 'upload' && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                          {uploadedResumeFile && (
                            <div style={{ fontSize: '0.85rem', color: '#9370db', padding: '6px', background: 'rgba(147,112,219,0.1)', borderRadius: '4px' }}>
                              📄 {uploadedResumeFile.name}
                            </div>
                          )}
                          <label htmlFor="resume-upload" style={{
                            padding: '6px 12px', background: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
                            color: 'white', borderRadius: '6px', cursor: isProcessingFile ? 'not-allowed' : 'pointer',
                            fontSize: '0.85rem', opacity: isProcessingFile ? 0.6 : 1
                          }}>
                            {isProcessingFile ? 'Processing...' : '📄 Upload Resume'}
                          </label>
                          <input id="resume-upload" type="file" accept=".txt,.doc,.docx,.pdf"
                            onChange={(e) => handleFileUpload(e, 'resume')} disabled={isProcessingFile} style={{ display: 'none' }} />
                        </div>
                        <textarea name="resumeText" placeholder="Paste your resume content or upload a file (PDF, DOCX, DOC, TXT)"
                          value={formData.resumeText} onChange={handleInputChange} rows="8" required disabled={isProcessingFile}></textarea>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      {uploadedResumeFile && (
                        <div style={{ fontSize: '0.85rem', color: '#9370db', padding: '6px', background: 'rgba(147,112,219,0.1)', borderRadius: '4px' }}>
                          📄 {uploadedResumeFile.name}
                        </div>
                      )}
                      <label htmlFor="resume-upload" style={{
                        padding: '6px 12px', background: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
                        color: 'white', borderRadius: '6px', cursor: isProcessingFile ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem', opacity: isProcessingFile ? 0.6 : 1, marginLeft: 'auto'
                      }}>
                        {isProcessingFile ? 'Processing...' : '📄 Upload Resume'}
                      </label>
                      <input id="resume-upload" type="file" accept=".txt,.doc,.docx,.pdf"
                        onChange={(e) => handleFileUpload(e, 'resume')} disabled={isProcessingFile} style={{ display: 'none' }} />
                    </div>
                    <textarea name="resumeText" placeholder="Paste your resume content or upload a file (PDF, DOCX, DOC, TXT)"
                      value={formData.resumeText} onChange={handleInputChange} rows="8" required disabled={isProcessingFile}></textarea>
                  </>
                )}
              </div>
              
              {fileUploadError && (
                <div style={{
                  padding: '10px',
                  marginBottom: '15px',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  background: fileUploadError.startsWith('✓') 
                    ? 'rgba(76, 175, 80, 0.1)' 
                    : 'rgba(244, 67, 54, 0.1)',
                  color: fileUploadError.startsWith('✓') ? '#4caf50' : '#f44336',
                  border: `1px solid ${fileUploadError.startsWith('✓') ? '#4caf50' : '#f44336'}`
                }}>
                  {fileUploadError}
                </div>
              )}
              
              <button type="submit" className="cta-button" disabled={isProcessingFile}>
                {isEditing ? 'Update Information' : 'Start Interview (1 Credit)'}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="neural-background"></div>
      
      <div className="geometric-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>
      
      <div className="interview-page-container">
        
        <Header 
          setShowAuthModal={setShowAuthModal}
          onNavigate={handleHeaderNavigation}
          interviewInProgress={interviewStarted}
        />
        <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

        {showInsufficientCreditsModal && (
          <div className="modal-overlay" onClick={() => setShowInsufficientCreditsModal(false)}>
            <div className="modal-content glass" onClick={(e) => e.stopPropagation()}>
              <h3>Insufficient Credits</h3>
              <p>You need at least 1 credit to start an interview session.</p>
              <p>Current credits: <strong>{credits}</strong></p>
              <div className="modal-actions">
                <button className="modal-btn primary" onClick={() => {
                  setShowInsufficientCreditsModal(false);
                  document.dispatchEvent(new CustomEvent('openPricingModal'));
                }}>
                  Get More Credits
                </button>
                <button className="modal-btn" onClick={() => setShowInsufficientCreditsModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <main className="interview-page-main">
          <div className="li-hero">
            <div className="li-hero-badge">🚀 AI-Powered</div>
            <h1 className="li-hero-title">
              AI <span className="li-gradient-text">Interview Assistant</span>
            </h1>
            <p className="li-hero-subtitle">
              Get real-time AI interview assistance that helps you answer confidently and stand out to recruiters.
            </p>
          </div>
          <div className="interview-main-container">
            <div className="form-section">
              <div className="interview-form-container glass">
                {interviewStarted ? (
                  <>
                    {!isElectronApp && (
                    <div className="screen-share-section">
                      {screenShareError && (
                        <div className="screen-share-error">
                          {screenShareError}
                        </div>
                      )}
                      
                      <div className="screen-share-preview">
                        <video 
                          ref={screenShareRef} 
                          autoPlay 
                          muted 
                          playsInline
                          style={{
                            width: '100%',
                            borderRadius: '8px',
                            border: isScreenSharing ? '2px solid #e0a3ff' : 'none',
                            display: isScreenSharing ? 'block' : 'none'
                          }}
                        />
                        {!isScreenSharing && (
                          <div className="screen-share-placeholder">
                            <p>No screen sharing active</p>
                            <p className="small-text">Click "Share Screen" to begin</p>
                          </div>
                        )}
                      </div>
                      <div className="screen-share-controls">
                        {!isScreenSharing ? (
                          <button 
                            onClick={handleScreenShare} 
                            className="screen-share-btn"
                            title="Share your screen with the interviewer"
                          >
                            <span className="share-icon">🖥️</span>
                            Share Screen
                          </button>
                        ) : (
                          <button 
                            onClick={stopScreenShare} 
                            className="stop-share-btn"
                            title="Stop sharing your screen"
                          >
                            <span className="share-icon">⏹️</span>
                            Stop Sharing
                          </button>
                        )}
                      </div>
                    </div>
                    )}

                    <div className="job-info-display">
                      <div className="job-info-header">
                        <h3>Job Information</h3>
                        <button onClick={handleEditJobInfo} className="edit-job-btn">
                          Edit
                        </button>
                      </div>
                      
                      <div className="job-detail">
                        <span className="job-detail-label">Position:</span>
                        <span className="job-detail-value">{formData.jobRole}</span>
                      </div>
                      
                      <div className="job-detail">
                        <span className="job-detail-label">Experience:</span>
                        <span className="job-detail-value">
                          {formData.experienceLevel === 'entry' && 'Entry Level'}
                          {formData.experienceLevel === 'mid' && 'Mid Level'}
                          {formData.experienceLevel === 'senior' && 'Senior Level'}
                          {formData.experienceLevel === 'executive' && 'Executive'}
                        </span>
                      </div>
                      
                      {formData.company && (
                        <div className="job-detail">
                          <span className="job-detail-label">Company:</span>
                          <span className="job-detail-value">{formData.company}</span>
                        </div>
                      )}
                      
                      <div className="job-detail">
                        <span className="job-detail-label">Job Description:</span>
                        <span className="job-detail-value">
                          {formData.jobDescription.length > 100 
                            ? `${formData.jobDescription.substring(0, 100)}...` 
                            : formData.jobDescription}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="form-subtitle">Enter your details to customize the interview experience</p>

                    <button
                      onClick={() => {
                        const hasProfileResume = profileData && (
                          profileData.professionalSummary ||
                          profileData.skills ||
                          (Array.isArray(profileData.workExperience) && profileData.workExperience.some(e => e.company || e.position))
                        );
                        if (hasProfileResume && !resumeChoice) {
                          setResumeChoice('profile');
                          setFormData(prev => ({ ...prev, resumeText: buildResumeTextFromProfile(profileData) }));
                        } else if (!hasProfileResume && !resumeChoice) {
                          setResumeChoice('upload');
                        }
                        setShowJobForm(true);
                      }}
                      className="start-interview-btn"
                    >
                      Enter Job Information
                    </button>
                  </>
                )}
              </div>
            </div>

            {interviewStarted && <div className="interview-section active">
              <div className="interview-container">
                <div className="interview-header">
                  <h2>Interview for: {formData?.jobRole || 'Your Position'}</h2>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {interviewStarted && (
                      <>
                      {isElectronApp && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '8px 16px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}>
                          <span style={{ 
                            color: 'white', 
                            fontSize: '0.9rem',
                            fontWeight: isAutoMode ? 'normal' : 'bold'
                          }}>
                            Manual
                          </span>
                          <label style={{
                            position: 'relative',
                            display: 'inline-block',
                            width: '50px',
                            height: '24px',
                            cursor: 'pointer'
                          }}>
                            <input
                              type="checkbox"
                              checked={isAutoMode}
                              onChange={handleModeToggle}
                              style={{ opacity: 0, width: 0, height: 0 }}
                            />
                            <span style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: isAutoMode ? '#9370db' : '#ccc',
                              borderRadius: '24px',
                              transition: '0.4s',
                              cursor: 'pointer'
                            }}>
                              <span style={{
                                position: 'absolute',
                                content: '',
                                height: '18px',
                                width: '18px',
                                left: isAutoMode ? '29px' : '3px',
                                bottom: '3px',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                transition: '0.4s'
                              }}></span>
                            </span>
                          </label>
                          <span style={{ 
                            color: 'white', 
                            fontSize: '0.9rem',
                            fontWeight: isAutoMode ? 'bold' : 'normal'
                          }}>
                            Auto
                          </span>
                        </div>
                      )}

                        {isElectronApp && isAutoMode && (
                          <div style={{
                            padding: '8px 16px',
                            background: isAutoRecording ? 'rgba(0, 255, 0, 0.2)' : 'rgba(255, 165, 0, 0.2)',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '0.9rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            border: `1px solid ${isAutoRecording ? '#00ff00' : '#ffa500'}`
                          }}>
                            <span style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: isAutoRecording ? '#00ff00' : '#ffa500',
                              animation: isAutoRecording ? 'pulse 1.5s infinite' : 'none'
                            }}></span>
                            {isAutoRecording ? 'Listening...' : 'Waiting...'}
                          </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: '4px 10px' }}>
                          <span style={{ fontSize: 11, color: '#c4b5fd', userSelect: 'none' }}>A</span>
                          <input
                            type="range" min="11" max="22" value={fontSize}
                            onChange={e => setFontSize(Number(e.target.value))}
                            title={`Text size: ${fontSize}px`}
                            style={{ width: 70, accentColor: 'rgb(247 110 186)', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 15, color: '#c4b5fd', userSelect: 'none' }}>A</span>
                        </div>

                        <button
                          onClick={handleMicClick}
                          disabled={isLoading || isAutoMode}
                          title={micActive ? 'Stop recording' : 'Record interviewer question'}
                          style={{
                            width: 44, height: 44,
                            borderRadius: '50%',
                            border: 'none',
                            cursor: isLoading || isAutoMode ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: micActive ? '#22c55e' : '#ef4444',
                            boxShadow: micActive ? '0 0 0 0 rgba(34,197,94,0.7)' : 'none',
                            animation: micActive ? 'micPulse 1.5s infinite' : 'none',
                            transition: 'background 0.2s',
                            opacity: isLoading || isAutoMode ? 0.5 : 1,
                          }}
                        >
                          {micActive ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                              <path d="M12 1a4 4 0 0 1 4 4v7a4 4 0 0 1-8 0V5a4 4 0 0 1 4-4z"/>
                              <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/>
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                              <line x1="1" y1="1" x2="23" y2="23"/>
                              <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V5a3 3 0 0 0-5.94-.6"/>
                              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
                              <line x1="12" y1="19" x2="12" y2="23"/>
                              <line x1="8" y1="23" x2="16" y2="23"/>
                            </svg>
                          )}
                        </button>
                        <div style={{
                          padding: '8px 16px',
                          background: timeRemaining < 300 ? '#ff6b6b' : 'none',
                          borderRadius: '8px',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1.1rem'
                        }}>
                          {formatTime(timeRemaining)}
                        </div>
                      </>
                    )}
                    <button onClick={() => handleEndInterview(false)} className="end-button">
                      End Session
                    </button>
                  </div>
                </div>
                
                <div className="conversation-container" ref={conversationContainerRef}>
                  {autoRecordingError && (
                    <div style={{
                      padding: '12px',
                      background: 'rgba(255, 0, 0, 0.1)',
                      border: '1px solid rgba(255, 0, 0, 0.3)',
                      borderRadius: '8px',
                      color: '#ff6b6b',
                      marginBottom: '10px'
                    }}>
                      {autoRecordingError}
                    </div>
                  )}

                  {!interviewStarted ? (
                    <div className="welcome-message">
                      <h3>Complete the form to begin your interview</h3>
                      <p>Click "Enter Job Information" on the left side to begin.</p>
                      <p>Once started, I'll help you with both technical and behavioral interview questions.</p>
                    </div>
                  ) : conversation.length === 0 ? (
                    <div className="welcome-message">
                      <h3>Ready to begin your interview!</h3>
                      {isAutoMode ? (
                        <>
                          <p>Auto mode is active. The system will automatically detect and transcribe interviewer questions.</p>
                          <p>Make sure {loopbackDeviceName} is configured as your audio input device to capture system audio.</p>
                          <p>When the interviewer speaks, their question will be automatically transcribed and answered.</p>
                        </>
                      ) : (
                        <>
                          <p>When the interviewer asks a question, click "Record Interviewer Question" below.</p>
                          <p>I'll generate appropriate responses based on your resume and the job requirements.</p>
                        </>
                      )}
                      <p>You have 30 minutes for this session.</p>
                    </div>
                  ) : (
                    [...conversation].reverse().map((item) => (
                      <div key={item.id} className={`message ${item.sender}`}>
                        <div className="message-header">
                          <strong>{item.sender === 'ai' ? 'Suggested Response' : 'Interviewer Question'}</strong>
                          <span className="timestamp">{item.timestamp}</span>
                        </div>
                        <div className="message-content" style={{ fontSize: `${fontSize}px`, lineHeight: 1.65 }}>
                          {renderMessageContent(item.message, item.formattedMessage)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                
                {isElectronApp && isAutoMode && interviewStarted && (
                  <div style={{
                    marginTop: '15px',
                    padding: '12px',
                    background: 'rgba(147, 112, 219, 0.1)',
                    border: '1px solid rgba(147, 112, 219, 0.3)',
                    borderRadius: '8px',
                    color: '#e0a3ff',
                    textAlign: 'center',
                    fontSize: '0.9rem'
                  }}>
                    <strong>Auto Mode Active:</strong> The system is listening for interviewer questions via {loopbackDeviceName} audio routing.
                    Questions will be automatically transcribed and answered.
                  </div>
                )}
              </div>
            </div>}
          </div>

          <section className="info-section glass">
            <h2 className="info-section-title">Why Use AI Interview Assistant?</h2>
            <div className="info-grid">
              <div className="info-card glass">
                <div className="info-icon">🎯</div>
                <h3>Real-Time Response Generation</h3>
                <p>Get instant, contextually relevant answers to interview questions based on your resume and the job description. Practice with AI-powered suggestions that sound natural and professional.</p>
              </div>
              <div className="info-card glass">
                <div className="info-icon">💡</div>
                <h3>Personalized to Your Profile</h3>
                <p>Our AI analyzes your resume and the specific job requirements to generate responses that highlight your relevant experience and skills for each question.</p>
              </div>
              <div className="info-card glass">
                <div className="info-icon">🚀</div>
                <h3>Build Confidence</h3>
                <p>Practice answering tough interview questions in a safe environment. Learn how to articulate your experiences effectively and prepare for common behavioral and technical questions.</p>
              </div>
            </div>
          </section>

          <section className="how-it-works-section glass">
            <h2 className="info-section-title">How It Works</h2>
            <div className="steps-grid">
              <div className="step-card glass">
                <div className="step-number">1</div>
                <h4>Enter Job Details</h4>
                <p>Provide the job role, experience level, company name, and job description (or upload a file) to customize the interview experience for your specific opportunity.</p>
              </div>
              <div className="step-card glass">
                <div className="step-number">2</div>
                <h4>Upload Your Resume</h4>
                <p>Upload your resume file (PDF, DOCX, DOC, or TXT) or paste the text so the AI can understand your background, skills, and experiences to generate personalized responses.</p>
              </div>
              <div className="step-card glass">
                <div className="step-number">3</div>
                <h4>Record Questions</h4>
                <p>Use the audio recorder to capture interview questions in real-time. Our AI will transcribe the question automatically.</p>
              </div>
              <div className="step-card glass">
                <div className="step-number">4</div>
                <h4>Get AI Responses</h4>
                <p>Receive intelligent, contextual answers that you can use as inspiration or speak directly during your interview.</p>
              </div>
            </div>
          </section>

          <section className="tips-section glass">
            <h2 className="info-section-title">Interview Success Tips</h2>
            <div className="tips-columns">
              <div className="tip-column glass">
                <h4>Before the Interview</h4>
                <ul>
                  <li>Review the job description thoroughly and identify key requirements</li>
                  <li>Prepare your resume with accurate, up-to-date information</li>
                  <li>Research the company culture and recent news</li>
                  <li>Practice common behavioral questions using the STAR method</li>
                  <li>Test your audio and screen sharing before the interview</li>
                  <li>Prepare questions to ask the interviewer</li>
                </ul>
              </div>
              <div className="tip-column glass">
                <h4>During the Interview</h4>
                <ul>
                  <li>Use AI suggestions as inspiration, not scripts - sound natural</li>
                  <li>Listen carefully to questions before responding</li>
                  <li>Provide specific examples from your experience</li>
                  <li>Keep answers concise but thorough (2-3 minutes max)</li>
                  <li>Show enthusiasm and genuine interest in the role</li>
                  <li>Ask clarifying questions if you don't understand something</li>
                </ul>
              </div>
              <div className="tip-column glass">
                <h4>Best Practices</h4>
                <ul>
                  <li>Adapt AI responses to match your personal communication style</li>
                  <li>Focus on achievements with quantifiable results</li>
                  <li>Be honest - don't claim experience you don't have</li>
                  <li>Demonstrate problem-solving skills with concrete examples</li>
                  <li>Show how you handle challenges and learn from failures</li>
                  <li>Follow up with a thank-you email after the interview</li>
                </ul>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default InterviewPage;